/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import { equal } from "helpers/toolsHelper";
import { useDataParams } from "hooks/DataParamsHook";
import React, { useContext, createContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { TextsContext } from "./textsContext";
import { clearTokens, getAuthToken, saveTokens } from "../api/authTokens";
import { agentAuthenticatedLogin } from "../api/loginApi";

const userContext = createContext();

// Los agentes que entran por params (iframe) no traen `name`; sólo el login por
// usuario/contraseña lo aporta. Va con formato "Nombre Apellido" para que el navbar
// y el home lo muestren ("Agente Invitado", iniciales "AI") sin cambiar su lógica.
const DEFAULT_AGENT_NAME = 'Agente Invitado';

/**
 * Devuelve el `userdata` guardado para reutilizar sus campos de perfil, o `{}` si
 * pertenece a otro usuario. Un campo de identidad ausente en lo guardado no cuenta
 * como contradicción: el payload del login no trae agentId/userId, y tratarlos como
 * distintos descartaba un registro que sí era del mismo agente.
 */
const getStoredProfile = (userdata, identity) => {
    if (!userdata) return {};

    let parsed;
    try {
        parsed = JSON.parse(userdata);
    } catch (error) {
        console.error('Error parsing stored userdata:', error);
        return {};
    }

    const isOtherUser = Object.entries(identity).some(
        ([field, value]) => parsed?.[field] !== undefined && !equal(value, parsed[field])
    );

    return isOtherUser ? {} : parsed;
};

export function useAuth() {
    return useContext(userContext);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(false);
    const [loading, setLoading] = useState(true);
    const { data, loading: paramLoading } = useDataParams();
    const { texts, loaded } = useContext(TextsContext);
    const queryClient = useQueryClient();
    const attemptedLogin = useRef(null);

    // Autentica contra el backend al agente que llega por params (iframe) para obtener sus tokens.
    // Mientras responde `loading` sigue en true, así el guard no manda a /login antes de tiempo.
    // Si falla no se crea sesión: sin token las llamadas al backend irían sin autorizar.
    const loginAgent = async (user_) => {
        try {
            const tokens = await agentAuthenticatedLogin({ ...user_, lang: data?.lang || 'en' });
            const agent = { ...user_, name: user_.name || DEFAULT_AGENT_NAME };
            clearTokens();
            saveTokens(tokens);
            sessionStorage.setItem('userdata', JSON.stringify(agent));
            localStorage.setItem('userdata', JSON.stringify(agent));
            setUser(agent);
        } catch (error) {
            console.error('Error authenticating agent:', error);
        } finally {
            setLoading(false);
        }
    }

    // autoLog corre en cada cambio de sus dependencias; se intenta una sola vez por identidad
    // para no repetir la petición mientras está en vuelo ni reintentar tras un fallo.
    const attemptLogin = (user_) => {
        const key = JSON.stringify([user_.agentId, user_.userId, user_.agentNumber, user_.apQuoteId]);
        if (attemptedLogin.current === key) return;
        attemptedLogin.current = key;
        loginAgent(user_);
    }

    const autoLog = () => {
        if (!user && !paramLoading) {
            const userdata = sessionStorage.getItem('userdata') ?? localStorage.getItem('userdata');
            const agentId = (data?.agentId || data?.AgentId) ?? 0;
            const userId = (data?.userId || data?.UserId) ?? 0;
            const onAgents = data?.onAgents;
            const agentNumber = data?.agentNumber || data?.AgentNumber;
            const apQuoteId = data?.apQuoteId || 0

            if (onAgents && onAgents !== "0") {
                localStorage.setItem('source', "AC_APP");
            } else {
                localStorage.setItem('source', "web");
            }

            let sameValues = true;
            if ((agentId || userId || agentNumber || apQuoteId) && userdata) {
                const parsed = JSON.parse(userdata);
                sameValues = equal(agentId, parsed?.agentId) && equal(userId, parsed?.userId) && equal(agentNumber, parsed?.agentNumber) && equal(apQuoteId, parsed?.apQuoteId);
            }

            const hasParamsIdentity = agentId != 0 || userId != 0 || !!agentNumber;

            if (userdata && sameValues) {
                const stored = JSON.parse(userdata);
                // Sesión previa del mismo agente pero sin token (p. ej. otra pestaña: los tokens
                // viven en sessionStorage): se vuelve a autenticar.
                if (hasParamsIdentity && !getAuthToken()) {
                    attemptLogin(stored);
                    return;
                }
                setUser(stored);
            } else {
                if (!agentId && agentId !== 0 && !userId && userId !== 0) {
                    setLoading(paramLoading);
                    return;
                };
                if (agentId == 0 && userId == 0 && !agentNumber) {
                    setLoading(paramLoading);
                    return;
                }
                // Los params sólo traen identidad; el perfil (name, token, ...) lo aporta
                // el login por usuario/contraseña. Se conserva salvo que lo guardado sea
                // de otra persona: como `equal` da false ante un campo ausente, sólo
                // pueden contradecir los campos de identidad que el registro sí define
                // (si no, entrar con ?key= tras loguearse borraba el nombre del navbar).
                // apQuoteId queda fuera: otra cotización no es otro usuario.
                const storedProfile = getStoredProfile(userdata, { agentId, userId, agentNumber });

                const user_ = { ...storedProfile, agentId, userId, agentNumber, apQuoteId };
                attemptLogin(user_);
                return;
            }
        }
        setLoading(paramLoading)
    }

    useEffect(() => {
        try {
            if (!(loaded && JSON.parse(texts?.des_key) === true)) {
                autoLog();
            } else {
                setUser(false);
            }
        } catch (error) {
            autoLog();
        }
    }, [user, data, paramLoading, loaded, texts])

    const logout = () => {
        setUser(false);
        localStorage.removeItem('userdata');
        sessionStorage.removeItem('userdata');
        clearTokens();
        // Limpia el cache de react-query para que la sesión siguiente no vea
        // datos del usuario anterior (dashboard, métricas, etc.).
        queryClient.clear();
    }

    const clearApQuoteId = () => {
        setUser(prev => {
            if (!prev) return prev;
            const { apQuoteId, ...rest } = prev;
            return rest;
        });

        [localStorage, sessionStorage].forEach(store => {
            const raw = store.getItem('userdata');
            if (!raw) return;
            try {
                const parsed = JSON.parse(raw);
                delete parsed.apQuoteId;
                store.setItem('userdata', JSON.stringify(parsed));
            } catch (error) {
                console.error('Error clearing apQuoteId from userdata:', error);
            }
        });
    }

    const loginWithUserData = (agentNumber) => {
        if (!agentNumber) return false;
        const user_ = { agentId: 0, userId: 0, agentNumber };
        setUser(user_);
        sessionStorage.setItem('userdata', JSON.stringify(user_));
        localStorage.setItem('userdata', JSON.stringify(user_));
        localStorage.setItem('source', "link");
        return true;
    }

    // Derived name helpers, exposed so consumers don't re-implement this.
    const nameParts = (user?.name ?? '').trim().split(/\s+/).filter(Boolean);
    const displayName = nameParts.length >= 2 ? `${nameParts[0]} ${nameParts[1]}` : nameParts[0] || '';
    const initials = nameParts.length >= 2
        ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
        : nameParts[0]?.[0]?.toUpperCase() ?? '-';

    const value = {
        user,
        setUser,
        logout,
        loginWithUserData,
        displayName,
        initials,
        loading,
        clearApQuoteId
    }
    return <userContext.Provider value={value}>{children}</userContext.Provider>;
}
